import { useCallback } from 'react';
import { getPlayerLimits } from 'src/services/limitsApi';
import { openDialog } from 'src/store/dialog/actions';
import { EnumDialogsKeys } from 'src/store/dialog/types';
import {
  setTotalActivityLimitValue,
  setTotalBetLimitValue,
  setTotalDepositValue,
  setTotalLossLimitValue,
} from 'src/store/user/actions';
import { useConnectedAction } from './use-connected-action';

type UseCurrentPlayerLimitsReturn = {
  getCurrentPlayerLimits: () => Promise<void>;
};

export const useCurrentPlayerLimits = (): UseCurrentPlayerLimitsReturn => {
  const _openDialog = useConnectedAction(openDialog);
  const _setTotalActivityLimitValue = useConnectedAction(setTotalActivityLimitValue);
  const _setTotalBetLimitValue = useConnectedAction(setTotalBetLimitValue);
  const _setTotalLossLimitValue = useConnectedAction(setTotalLossLimitValue);
  const _setTotalDepositValue = useConnectedAction(setTotalDepositValue);

  const isEmpty = (arr?: unknown[]): boolean => !Array.isArray(arr) || arr.length === 0;

  const setLimitValue = (
    active: { limitValue: number }[] | undefined,
    setter: (value: number) => void,
    storageKey: string
  ): void => {
    if (!isEmpty(active) && active) {
      const value = active[0].limitValue;
      setter(value);
      localStorage.setItem(storageKey, String(value));
    }
  };

  const getCurrentPlayerLimits = useCallback(async () => {
    const res = await getPlayerLimits();
    if (!res?.success) return;

    const { totalActivity, totalBet, totalLoss, totalDeposit } = res.result;

    setLimitValue(totalActivity?.active, _setTotalActivityLimitValue, 'totalActivityLimit');
    setLimitValue(totalBet?.active, _setTotalBetLimitValue, 'totalBetLimit');
    setLimitValue(totalLoss?.active, _setTotalLossLimitValue, 'totalLossLimit');
    setLimitValue(totalDeposit?.active, _setTotalDepositValue, 'totalDepositLimit');
    if (
      isEmpty(totalActivity?.active) ||
      isEmpty(totalBet?.active) ||
      isEmpty(totalLoss?.active) ||
      isEmpty(totalDeposit?.active)
    ) {
      _openDialog({
        dialogType: EnumDialogsKeys.SET_LIMITS_AFTER_LOGIN_POPUP,
        dialogProps: { data: {} },
      });
    }
  }, [
    _openDialog,
    _setTotalActivityLimitValue,
    _setTotalBetLimitValue,
    _setTotalLossLimitValue,
    _setTotalDepositValue,
  ]);

  return {
    getCurrentPlayerLimits,
  };
};
